import Accordion from '../../common/components/Accordion/Accordion'
import MarginDecoration from '../../common/components/MarginDecoration/MarginDecoration'
import PageLayout from '../../common/components/PageLayout/PageLayout'
import PageLink from '../../common/components/PageLink/PageLink'
import References from '../../common/components/References/References'
import TextRef from '../../common/components/TextRef/TextRef'
import TextSeparator from '../../common/components/TextSeparator/TextSeparator'
import Title from '../../common/components/Title/Title'
import './Engineering.css'

function Engineering () {
  return (
    <PageLayout id="engineering" title="Engineering">
      <MarginDecoration />
      <Title title="Context" />
      <p>
        Our main project goal was to improve on past trials of enzymatic removal
        of blood antigens towards the processing of blood donations of any type
        into a universal blood product, O-type blood. In that regard, we decided
        to implement the enzymatic processes inside a fluidic tubular device to
        relieve the low efficiency of antigen removal and to develop a system
        that would be relevant in a clinical setting. To achieve this goal, we
        had multiple goals for wet and dry lab. For the wet lab section, we
        aimed to produce, purify and test enzymes able to convert A and B blood
        types into an O blood type. As for the dry lab, we simulated the flow
        and homogeneity of blood in our device to optimise its geometry.
        Additionally, we set out to compute the first 3D structure of the human
        Rh complex with its most immunogenic subunit, RhD.
      </p>
      <br />
      <br />
      <Title title="Design" />
      <br />
      <Accordion title="Wet lab">
        <p>
          The foundation of our 2023 project was built upon the enzymes
          previously used by the Taipei 2021 team, the same enzymes used in past
          assays of enzymatic removal of blood antigens
          <TextRef sup="1" content="" />. These enzymes were used as a positive
          control and a comparison tool for our newly designed enzymes. These
          enzymes, the α-N-acetyl-galactosaminidase (NAGA) from{' '}
          <i>Elizabethkingia meningosepticum</i> <TextRef sup="2" content="" />{' '}
          and α-galactosidase (GH110a) from <i>Bacteriodes fragilis</i>
          <TextRef sup="3" content="" />, have the potential to transform any
          blood type into O type blood when used together.
        </p>
        <p>
          The NAGA from <i>E. meningosepticum</i> has a molecular weight of 50.3
          kDa and converts respectively A and AB type blood into O and B type
          blood. It requires, however, NAD cofactors in order to operate and to
          perform several redox cycles for the enzyme turn-over, as well as a
          matrix like dextran to bring blood cells and the enzymes closer
          together. Furthermore, complete removal of the A antigen requires
          large amounts of proteins per blood cells unit
          <TextRef sup="4" content="" />.
        </p>
        <p>
          The GH110a from <i>B. fragilis</i> has a molecular weight of 68.2 kDa
          and can convert respectively B and AB type blood into O and A type
          blood. This enzyme operates without the need of cofactors and operates
          30 times more efficiently to remove B antigen than NAGA does to remove
          A antigen <TextRef sup="4" content="" />.
        </p>
        <p>
          Considering past work and limitations, we set out to use instead
          enzymes not requiring cofactors or an additional matrix to perform
          efficiently, and that could also be functional in physiological
          condition. In that regard, two A antigen targeting enzymes from the
          human gut microbiome had been identified to work in pair to
          efficiently remove the N-acetyl-α-D-galactosamine moeity of A antigen
          leading, similarly to NAGA, to H antigen most commonly recognised has
          O type blood <TextRef sup="5" content="" />. These enzymes from{' '}
          <i>Flavonifractor plautii</i>, namely N-acetyl-α-D-galactosamine
          deacetylase (that we named P0DTR4 throughout this work) and
          α-D-galactosamine galactosaminidase (that we named P0DTR5), showed to
          be active and performing well whereas NAGA failed to show any activity
          unless dextran was added. Both were also functional at physiological
          condition. However, they work strictly in pair as P0DTR5 does not
          display any A antigen degradation activity without P0DTR4.
        </p>
        <p>
          While GH110a from <i>B. fragilis</i> was shown to be performing well,
          a second efficient α-galactosidase (GH110b) was identified in the same
          report <TextRef sup="3" content="" />. The latter had a similar
          affinity and catalytic activity towards B antigen but was also active
          towards both branched and linear B antigen, whereas GH110a is only
          towards branched B antigen <TextRef sup="3" content="" />. This could
          prove to be promising for xenotransplantation purposes
          <TextRef sup="3" content="" />.
        </p>
        <p>
          We thus selected the enzymes P0DTR4-P0DTR5 and GH110b for our goal of
          removing ABO type antigen from blood donations. NAGA and GH110a were
          used in this work as control of our experiments and standard of
          enzymatic removal of antigen. 6xHis-tag were added to codon optimised
          DNA sequences. Restriction sites were added for directional cloning
          purposes onto the expression plasmid pET-3a; <i>NdeI</i> in 5’ and{' '}
          <i>BamHI</i> in 3’ of construct sequences.
        </p>
      </Accordion>
      <br />
      <Accordion title="Dry lab">
        <Title title="Protein structure prediction" isSub />
        <p>
          The Rhesus system is also a crucial blood type that can lead to
          serious adverse effects during transfusions if it is not accounted
          for. Its most immunogenic subunit is RhD. However, very little is
          known about its 3D structure in its biological context, as it has
          never before been modelled. We therefore opted to adopt a{' '}
          <i>de novo</i> approach using homology elements from the Rh protein
          family. This means that we needed to gather a large amount of
          information pertaining to the proteins we aimed to model. We found the
          sequence of Rh family proteins and performed sequence alignments. We
          also predicted the secondary structure of these proteins so that we
          could have a basis with which to compare our models further down the
          line. Finally, the design part of the protein structure prediction of
          our project included learning the lipid composition of mammalian
          plasma membranes, which includes human red blood cells.
        </p>
        <Title title="Fluid dynamics" isSub />
        <p>
          The computational fluid dynamic (CFD) analyses were designed
          specifically to assess the effect that various parameters have on flow
          homogeneity within the fluidic system we had in mind. We came up with
          a zigzag system with a cross tube running through it. This tube
          creates notches in the system, improving the contact time between the
          enzymes and the red blood cells. The enzymes lining the system are
          NAGA antigen A and aGAL antigen B. We chose this type of fluid system
          because it provides an optimum contact surface between the enzymes and
          the erythrocytes’ surface, while being long enough for the blood to be
          processed efficiently. We decided to focus on channel width, distance
          between baffles, baffle length, and pressure as possibly important
          parameters due to their impact on systemgeometry and fluid flow
          velocity. The experiments were designed to maximise the range of
          values tested for each parameter in the least amount of experiments
          possible. This was done both to optimise our own time and to not run
          partially redundant calculations, which can be quite energy-consuming.
          The models were also chosen to be in 2D rather than in 3D partly to
          reduce the computational workload and thus the environmental impact of
          these analyses.
        </p>
      </Accordion>
      <br />
      <TextSeparator />
      <Title title="Build" />
      <br />
      <Accordion title="Wet lab">
        <ol>
          <li>
            <b>Restriction-Ligation Cloning</b>: We utilized
            restriction-ligation cloning techniques to insert our designed DNA
            fragments, which had been digested with <i>NdeI</i> and <i>BamHI</i>{' '}
            enzymes, into the pET-3a plasmid.
          </li>
          <li>
            <b>Purification from Agarose Gel</b>: After the restriction
            digestion, we purified the DNA fragments from the agarose gel to
            isolate the desired fragments for subsequent ligation.
          </li>
          <li>
            <b>Ligation Using T4 Ligase</b>: The purified DNA fragments were
            then ligated into the pET-3a plasmid using T4 ligase, creating
            recombinant plasmids containing our constructs.
          </li>
          <li>
            <b>DH5α Transformation</b>: Following ligation, we transformed the
            ligated plasmids into <i>Escherichia coli</i> DH5α cells to amplify
            and verify the presence of the plasmid constructs.
          </li>
          <li>
            <b>Diagnostic PCR</b>: To confirm the successful insertion of our
            constructs, we performed diagnostic PCR using both T7 promoter and
            terminator primers.
          </li>
          <li>
            <b>Plasmid Extraction & Purification</b>: After confirming the
            presence of the constructs, we extracted and purified the plasmids
            from the DH5α transformants, ensuring the availability of
            high-quality DNA for downstream applications.
          </li>
          <li>
            <b>Verified Transformants for Long Storage Stocks</b>: The
            transformants that were verified and contained the desired plasmid
            constructs were cultivated for long-term storage stocks, ensuring a
            stable source for future experiments.
          </li>
          <li>
            <b>Subsequent Plasmid Extraction and Purification</b>: Before
            proceeding to the final phase of our wet lab work, which involved
            protein expression in E. coli BL21 (DE3) chemically competent cells,
            we conducted an additional round of plasmid extraction and
            purification to ensure the integrity of our constructs and the
            availability of high-quality plasmid DNA for transformation into
            BL21 (DE3) cells. This step is crucial to guarantee the success of
            protein expression experiments.
          </li>
          <li>
            <b>BL21 Transformation</b>: After several cycles of troubleshooting,
            we changed our expression strains to <i>E. coli</i> Rosetta (DE3)
            pLysS due to proteotoxicity that was fatal in <i>E. coli</i> BL21.
            After transformation in <i>E. coli</i> Rosetta (DE3) pLysS, plasmid
            extraction was carried out to confirm the success of transformation.
            Only strains expressing P0DTR5, GH110b and GH110a were obtained
            overall. Strains were then cultivated for long-term storage stocks.
          </li>
        </ol>
      </Accordion>
      <br />
      <Accordion title="Dry lab">
        <Title title="Protein structure prediction" isSub />
        <p>
          The protein structures were predicted using AlphaFold 2 Multimer to
          create high quality models for the RhD, RhAG, and RhCE subunits, as
          well as all possible permutations of dimers and trimers, either
          homomers or heteromers. We predicted all possible permutations to
          limit bias and gain a better understanding of the interactions between
          the different Rh subunits.We did not predict tetramers because the
          literature shows no detected presence of Rh tetramers <i>in vivo</i>{' '}
          and to limit our use of computation resources usage.
        </p>
        <Title title="Molecular dynamics" isSub />
        <p>
          We used the data we gathered regarding the composition of mammalian
          plasma cell membranes and the 3D structure of our best Rh trimers to
          simulate a water and ions box approximating <i>in vivo</i> lipid
          composition and ion concentrations. This system, its parameters and
          its force field were created with Charmm-gui to get a high quality
          prediction.
        </p>
        <Title title="CFD" isSub />
        <p>
          The CFD models were built using a combination of geometric forms and
          meshes. The nine models were built using the ANSYS DesignModeler
          software. We then simulated the flow of blood through these 2D pipes
          using ANSYS Fluent.
        </p>
        <p>
          The models were designed with the parameters specified in{' '}
          <PageLink src="https://2023.igem.wiki/ulaval/model">Model</PageLink>{' '}
          in order to assess as wide of a range of values as possible and thus
          determine which parameters have an influence on flow homogeneity. We
          then successfully simulated blood flow through these 2D pipes. The
          specific parameters used to solve these simulations are also available
          on our
          <PageLink src="https://2023.igem.wiki/ulaval/model">
            Model
          </PageLink>{' '}
          page.
        </p>
      </Accordion>
      <br />
      <TextSeparator />
      <Title title="Test" />
      <br />
      <Accordion title="Wet lab">
        <p>
          With our <i>E. coli</i> Rosetta (DE3) pLysS carrying respectively
          pET-3a-P0DTR5, pET-3a-GH110b and pET-3a-GH110a were cultured for
          expression and IMAC purification assays. Qualitative Bradford assays
          and SDS-PAGE electrophoresis were carried out to confirm the presence
          and identity of our proteins. We thus obtained purified extract
          containing our proteins of interest but also residual contaminant
          proteins. Due to those contaminants, precise characterization of the
          specific activity of our enzymes and control was not possible. We
          still made a qualitative α-galactosidase to test whether GH110b and
          GH110a were active after expression and purification. It was confirmed
          that both were active and that GH110b might be more performant.
          However, further characterization is required (for further details,
          see page{' '}
          <PageLink src="https://2023.igem.wiki/ulaval/results">
            Results
          </PageLink>
          ).
        </p>
      </Accordion>
      <br />
      <Accordion title="Dry lab">
        <Title title="Protein structure prediction" isSub />
        <p>
          We verified the accuracy of our predicted protein structures using
          FoldX. This software allowed us to evaluate the binding energy
          required for each predicted dimer and trimer. This data can be seen as
          a proxy of how plausible it is for each of these models to occur{' '}
          <i>in vivo</i> regarding thermodynamics. The FoldX results also helped
          us confirm our qualitative evaluation that was made during the
          structure prediction phase of the stabilising function of the RhAG
          subunit .
        </p>
        <p>
          We also compared the predicted structure of the 2xRhAG with RhCE
          trimer we computed with a published cryo-EM structure
          <TextRef sup="6" content="" />. The RhCE is the only protein that had
          already been modelled in complex by other scientists, and as such the
          only predicted model with which we could verify the accuracy of our
          predictions. Additionally, we used the NAMD
          <TextRef sup="7" content="" /> and VMD <TextRef sup="8" content="" />{' '}
          molecular dynamics softwares to simulate the molecular dynamics of the
          stable heterotrimers of RhCE and RhD (2xRhAG with RhCE or RhD), which
          allowed us to evaluate its stability.
        </p>
        <Title title="CFD" isSub />
        <p>
          The results were analysed to determine which parameters have an impact
          on flow homogeneity. For this, we created channel diagrams and bar
          graphs for each model. These visualisations of the results allowed us
          to determine the effect of each parameter on homogeneity. Seeing as
          homogeneity is very hard to evaluate and there does not exist a
          standard method, we decided to rely on an assessment of velocity as it
          affects the Reynolds number and thus the type of flow and its
          homogeneity (see
          <PageLink src="https://2023.igem.wiki/ulaval/model">
            Project Modelling
          </PageLink>
          ).
        </p>
      </Accordion>
      <br />
      <TextSeparator />
      <Title title="Learn" />
      <br />
      <Accordion title="Wet and Dry lab">
        <p>
          Our tests revealed that all our proteins tested showed significant
          signs of proteotoxicity leading to cell death in the bacterial host.
          Although using <i>E. coli</i> Rosetta (DE3) pLysS seemed to resolve
          the problem, further expression conditions could be tried in order to,
          for example, increase the proportion of GH110a in the soluble extract.
          Furthermore, our enzyme showed to be active in controlled reactions,
          which does not exactly represent the conditions in the fluidic tubular
          device they would be in. This however gives us an indication that they
          would be at least working if we were to use solutions at physiological
          conditions of blood.
        </p>
        <Title title="Protein structure prediction" isSub />
        <p>
          Our prediction of the structure of the human Rh protein system
          revealed that the RhAG subunit seems to stabilise the complex and thus
          to play an important functional role. We also discovered that there
          are very few protein complex configurations with negative binding
          energy. This means that there are only a select few complexes that are
          biologically plausible. These results confirmed the predictions we
          obtained using AlphaFold2 Multimer.
        </p>
        <p>
          Our comparison of our predicted 2xRhAG-RhCE trimer structure with a
          published experimental structure in turn confirmed the accuracy of our
          methods. Both structures are extremely similar. As confirmed by our
          sequence alignments, secondary structure predictions and 3D models,
          the RhCE and RhD subunits are extremely similar, and are somewhat
          similar to the RhAG subunit. This gives us a high level of confidence
          that our predictions are equally accurate.
        </p>
        <Title title="Molecular dynamics" isSub />
        <p>
          Molecular dynamics analyses revealed that the complex moves within the
          membrane on the x axis. The complex is also relatively stable, in that
          it does not move vertically (its cytosolic and extracellular
          components remain firmly within their assigned spaces). These analyses
          lacked the simulation of an important protein complex, the human
          erythrocyte ankyrin-1 complex, that is normally always found with the
          Rh complex in physiological conditions. Our decision not to include
          this very large complex is simply due to material limitations, as it
          would demand too much computational power. The ankyrin-1 complex is
          exceptionally large, as it includes proteins such as ankyrin,
          spectrin, GPA and GPB <TextRef sup="6" content="" />.
        </p>
        <Title title="CFD" isSub />
        <p>
          The first round of analyses allowed us to realise that not only did
          pressure affect the flow through an increase in molecular viscosity
          due to the nature of blood (see{' '}
          <PageLink src="https://2023.igem.wiki/ulaval/model">
            Project Modelling
          </PageLink>
          ), but it also led to much greater velocity. This rendered our initial
          analysis plan to assess homogeneity through velocity inefficient, as
          velocity was very significantly greater with the use of greater
          pressure, almost independently of channel geometry. In order to solve
          this problem, we opted to compute all models again, but with a
          constant input pressure of 10 psi. This analysis in turn gave us
          interpretable results that allowed the identification of parameters
          that have a significant impact on flow homogeneity and gave us
          insights on how to modify them to maximise homogeneity.
        </p>
      </Accordion>

      <References
        references={[
          <span key={1}>
            Rahfeld P, Withers SG. Toward universal donor blood: Enzymatic
            conversion of A and B to O type. J Biol Chem. 2020 Jan
            10;295(2):325-334. doi: 10.1074/jbc.REV119.008164. Epub 2019 Dec 2.
            PMID: 31792054; PMCID: PMC6956546.
          </span>,
          <span key={2}>
            Liu QP, Sulzenbacher G, Yuan H, Bennett EP, Pietz G, Saunders K,
            Spence J, Nudelman E, Levery SB, White T, Neveu JM, Lane WS, Bourne
            Y, Olsson ML, Henrissat B, Clausen H. Bacterial glycosidases for the
            production of universal red blood cells. Nat Biotechnol. 2007
            Apr;25(4):454-64. doi: 10.1038/nbt1298. Epub 2007 Apr 1. PMID:
            17401360.
          </span>,
          <span key={3}>
            Liu QP, Yuan H, Bennett EP, Levery SB, Nudelman E, Spence J, Pietz
            G, Saunders K, White T, Olsson ML, Henrissat B, Sulzenbacher G,
            Clausen H. Identification of a GH110 subfamily of alpha
            1,3-galactosidases: novel enzymes for removal of the alpha 3Gal
            xenotransplantation antigen. J Biol Chem. 2008 Mar
            28;283(13):8545-54. doi: 10.1074/jbc.M709020200. Epub 2008 Jan 28.
            PMID: 18227066; PMCID: PMC2417185.
          </span>,
          <span key={4}>
            Fernandes HP, Cesar CL, Barjas-Castro Mde L. Electrical properties
            of the red blood cell membrane and immunohematological
            investigation. Rev Bras Hematol Hemoter. 2011;33(4):297-301. doi:
            10.5581/1516-8484.20110080. PMID: 23049321; PMCID: PMC3415751.
          </span>,
          <span key={5}>
            Rahfeld P, Sim L, Moon H, Constantinescu I, Morgan-Lang C, Hallam
            SJ, Kizhakkedathu JN, Withers SG. An enzymatic pathway in the human
            gut microbiome that converts A to universal O type blood. Nat
            Microbiol. 2019 Sep;4(9):1475-1485. doi: 10.1038/s41564-019-0469-7.
            Epub 2019 Jun 10. PMID: 31182795.
          </span>,
          <span key={6}>
            Vallese F, Kim K, Yen LY, Johnston JD, Noble AJ, Calì T, Clarke OB.
            Architecture of the human erythrocyte ankyrin-1 complex. Nat Struct
            Mol Biol. 2022 Jul;29(7):706-718. doi: 10.1038/s41594-022-00792-w.
            Epub 2022 Jul 14. PMID: 35835865; PMCID: PMC10373098.
          </span>,
          <span key={7}>
            Phillips JC, Hardy DJ, Maia JDC, et al. Scalable molecular dynamics
            on CPU and GPU architectures with NAMD. J Chem Phys.
            2020;153(4):044130. doi:10.1063/5.0014475. PMID: 32752662.
          </span>,
          <span key={8}>
            Humphrey, W., Dalke, A. and Schulten, K., &quot;VMD - Visual
            Molecular Dynamics&quot;, J. Molec. Graphics, 1996, vol. 14, pp.
            33-3
          </span>
        ]}
      />
    </PageLayout>
  )
}

export default Engineering
